import './App.css';
import { useEffect, useState, useRef } from 'react';

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleElements, setVisibleElements] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);

    // Intersection Observer for scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => new Set([...prev, entry.target.dataset.scroll]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all elements with data-scroll attribute
    const elements = document.querySelectorAll('[data-scroll]');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const isElementVisible = (id) => visibleElements.has(id);

  return (
    <div className="App">
      {/* Hero Section */}
      <section className={`hero ${isVisible ? 'fade-in' : ''}`}>
        <div className="container">
          <div className="hero-grid">
            {/* Left Content */}
            <div className="hero-content">
              <div className="logo-wrapper">
                <img src="/app_logo.png" alt="MCQEra Logo" className="logo-icon" />
              </div>
              
              <h1 className="hero-title">
                MCQEra
              </h1>

              <p className="hero-description">
                বিসিএস ও সরকারি চাকরির পরীক্ষার প্রস্তুতির জন্য একটি আধুনিক অনলাইন প্ল্যাটফর্ম।
                হাজারো MCQ প্রশ্ন, লাইভ পরীক্ষা, বিস্তারিত বিশ্লেষণ এবং র‍্যাংকিং সিস্টেম সহ সম্পূর্ণ প্রস্তুতির সমাধান।
              </p>

              <div className="tagline-highlight">
                <span className="highlight-text">স্মার্ট প্রস্তুতি, নিশ্চিত সাফল্য</span>
              </div>

              <div className="coming-badge">
                <div className="badge-pulse"></div>
                <span>শীঘ্রই আসছে</span>
              </div>
            </div>

            {/* Right Phone Mockup */}
            <div className="hero-phone">
              <div className="phone-mockup">
                <div className="phone-frame">
                  <div className="phone-notch"></div>
                  <div className="phone-screen">
                    <div className="screen-blur">
                      <div className="blur-pattern"></div>
                      <div className="blur-pattern pattern-2"></div>
                      <div className="blur-pattern pattern-3"></div>
                    </div>
                    <div className="screen-overlay">
                      <div className="overlay-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M13.13 22.19L11.5 18.36C13.07 17.78 14.54 17 15.9 16.09L13.13 22.19M5.64 12.5L1.81 10.87L7.91 8.1C7 9.46 6.22 10.93 5.64 12.5M21.61 2.39C21.61 2.39 16.66 .269 11 5.93C8.81 8.12 7.5 10.53 6.65 12.64C6.37 13.39 6.56 14.21 7.11 14.77L9.24 16.89C9.79 17.45 10.61 17.63 11.36 17.35C13.5 16.53 15.88 15.19 18.07 13C23.73 7.34 21.61 2.39 21.61 2.39M14.54 9.46C13.76 8.68 13.76 7.41 14.54 6.63S16.59 5.85 17.37 6.63C18.14 7.41 18.15 8.68 17.37 9.46C16.59 10.24 15.32 10.24 14.54 9.46M8.88 16.53L7.47 15.12L8.88 16.53M6.24 22L9.88 18.36C9.54 18.27 9.21 18.12 8.91 17.91L4.83 22H6.24M2 22H3.41L8.18 17.24L6.76 15.83L2 20.59V22M2 19.17L6.09 15.09C5.88 14.79 5.73 14.47 5.64 14.12L2 17.76V19.17Z"/>
                        </svg>
                      </div>
                      <h3 className="overlay-title">অপেক্ষা করুন</h3>
                      <p className="overlay-subtitle">স্মার্ট প্রস্তুতির পথে একসাথে</p>
                      <div className="overlay-badge">Coming Soon</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-gradient"></div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <div className="feature-grid">
            <div className={`feature-card ${isElementVisible('feature-1') ? 'scroll-fade-in' : ''}`} data-scroll="feature-1">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,10H19.5L14,4.5V10M5,3H15L21,9V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5C3,3.89 3.89,3 5,3M5,5V19H19V12H12V5H5Z"/>
                </svg>
              </div>
              <h3>অনলাইন পরীক্ষা</h3>
              <p>বিসিএস সহ সকল সরকারি চাকরির জন্য বিশেষভাবে তৈরি MCQ পরীক্ষা</p>
            </div>

            <div className={`feature-card ${isElementVisible('feature-2') ? 'scroll-fade-in' : ''}`} data-scroll="feature-2">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9,17H7V10H9M13,17H11V7H13M17,17H15V13H17M19.5,19.1H4.5V5H19.5M19.5,3H4.5C3.4,3 2.5,3.9 2.5,5V19.1C2.5,20.2 3.4,21.1 4.5,21.1H19.5C20.6,21.1 21.5,20.2 21.5,19.1V5C21.5,3.9 20.6,3 19.5,3Z"/>
                </svg>
              </div>
              <h3>বিস্তারিত বিশ্লেষণ</h3>
              <p>আপনার পারফরম্যান্স ট্র্যাক করুন এবং দুর্বল দিকগুলো চিহ্নিত করুন</p>
            </div>

            <div className={`feature-card ${isElementVisible('feature-3') ? 'scroll-fade-in' : ''}`} data-scroll="feature-3">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M12,10A2,2 0 0,0 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12A2,2 0 0,0 12,10Z"/>
                </svg>
              </div>
              <h3>লক্ষ্য অর্জন</h3>
              <p>সঠিক প্রস্তুতির মা7্যমে আপনার স্বপ্নের চাকরি পান</p>
            </div>

            <div className={`feature-card ${isElementVisible('feature-4') ? 'scroll-fade-in' : ''}`} data-scroll="feature-4">
              <div className="icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7,10L12,15L17,10H7Z"/>
                  <path d="M19,19H5V5H19M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
                  <path d="M13.09,11.95L12,13.5L10.91,11.95L10,10.5L12,8.5L14,10.5L13.09,11.95Z"/>
                </svg>
              </div>
              <h3>দ্রুত ও সহজ</h3>
              <p>যেকোনো ডিভাইস থেকে যেকোনো সময় অনুশীলন করুন</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="coming-soon">
        <div className="container">
          <div className={`soon-content ${isElementVisible('coming-soon') ? 'scroll-fade-in' : ''}`} data-scroll="coming-soon">
            <div className="pulse-dot"></div>
            <h2>শীঘ্রই আসছে</h2>
            <p className="soon-description">
              আপনার প্রস্তুতিকে আরও সহজ ও কার্যকর করতে কাজ করছি
              <br />
              MCQEra শীঘ্রই লঞ্চ হবে প্রিমিয়াম ফিচার সহ।
            </p>
            <div className="features-list">
              <div className="check-item">✓ হাজারো MCQ প্রশ্ন</div>
              <div className="check-item">✓ লাইভ পরীক্ষা</div>
              <div className="check-item">✓ র‍্যাংকিং সিস্টেম</div>
              <div className="check-item">✓ বিশেষজ্ঞদের গাইডলাইন</div>
            </div>
            <p className="stay-tuned">সাথে থাকুন!</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2026 MCQEra. সর্বস্বত্ব সংরক্ষিত।</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
